import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import UsersReports from './UsersReports'
import DevicesReports from './DevicesReports'
import AnalyticsReports from './AnalyticsReports'
import OtherReports from './OtherReports'
import DashboardHeader from '../common/DashboardHeader/DashboardHeader'
import WrappedTabs from '../common/WrappedTabs'
import Breadcrumb from '../common/Breadcrumb'
import { toast } from '../common/Toast'
import {
  hasAnalyticsReportsPagePermissions,
  hasRegisteredDevicesReportsPagePermissions,
  hasGeographicalDevicesReportsPagePermissions,
  hasSoldDevicesReportsPagePermissions,
  hasRemoteAccessReportsPagePermissions,
  hasRegisteredUsersReportsPagePermissions,
  hasFirmwareReportsPagePermissions,
} from '../../utils/permissionValidation'
import './internal-reports.scss'

const InternalReports = (props) => {
  const { t } = useTranslation()
  const history = useHistory()
  const location = useLocation()
  const [availablePages, setAvailablePages] = useState([])
  const [selectedPage, setSelectedPage] = useState(null)
  const permissionsSnapshot = useMemo(() => props.userPermissions, [props.userPermissions])
  
  useEffect(() => {
    const pages = []
    const hasAnalytics = hasAnalyticsReportsPagePermissions(permissionsSnapshot)
    const hasAnyDevice =
      hasRegisteredDevicesReportsPagePermissions(permissionsSnapshot) ||
      hasGeographicalDevicesReportsPagePermissions(permissionsSnapshot) ||
      hasSoldDevicesReportsPagePermissions(permissionsSnapshot) ||
      hasRemoteAccessReportsPagePermissions(permissionsSnapshot)
    const hasUsers = hasRegisteredUsersReportsPagePermissions(permissionsSnapshot)
    const hasOther = hasFirmwareReportsPagePermissions(permissionsSnapshot)
    
    if (hasAnalytics) {
      pages.push({
        key: 0,
        slug: 'analytics-reports',
        title: 'reports.analytics_reports',
        content: <AnalyticsReports />,
      })
    }
    
    if (hasAnyDevice) {
      pages.push({
        key: 1,
        slug: 'devices-reports',
        title: 'reports.devices_reports',
        content: <DevicesReports />,
      })
    }
    
    if (hasUsers) {
      pages.push({
        key: 2,
        slug: 'users-reports',
        title: 'reports.users_reports',
        content: <UsersReports />,
      })
    }
    
    if (hasOther) {
      pages.push({
        key: 3,
        slug: 'other-reports',
        title: 'reports.other_reports',
        content: <OtherReports />,
      })
    }
    
    setAvailablePages(pages)
    
    if (!pages.length) {
      setSelectedPage(null)
      return
    }
    
    const params = new URLSearchParams(location.search)
    const contentParam = params.get('content')
    let activePage = pages[0]
    
    if (contentParam) {
      const match = pages.find((p) => p.slug === contentParam)
      if (match) activePage = match
    }
    setSelectedPage(activePage)
    
    const expectedQuery = `?content=${activePage.slug}`
    if (location.search !== expectedQuery) {
      history.replace({ search: expectedQuery })
    }
  }, [permissionsSnapshot, location.search, history])
  
  const handleTabClick = (index) => {
    toast.dismiss()
    const page = availablePages.find((p) => p.key === index)
    if (!page) return
    setSelectedPage(page)
    history.push({ search: `?content=${page.slug}` })
  }

  return (
    <div className="internal-reports-wrapper">
      <Breadcrumb path={location.pathname} />
      <DashboardHeader headerText={t('nav.internal_reports')} />
      <WrappedTabs
        className="main-page-tabs-wrapper"
        tabs={availablePages.map((page) => ({ id: page.key, name: page.title }))}
        activeTab={selectedPage?.key}
        changeTab={handleTabClick}
      />
      {selectedPage?.content}
    </div>
  )
}

function mapStateToProps({ userPermissions }) {
  return {
    userPermissions,
  }
}

export default connect(mapStateToProps)(InternalReports)

